import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { message } from 'antd';
import { omit, uniq } from 'lodash';

import BaseForm from '../../../../../components/form/BaseForm';
import InputForm from '../../../../../components/form/InputForm';
import Select from '../../../../../components/input/Select/Select';
import Input from '../../../../../components/input/Input/Input';
import Button from '../../../../../components/Button';
import Table from '../../../../../components/table/Table';
import EditProductOptionOrderModal from '../../modal/EditProductOptionOrderModal';
import columns from './columns';
import { asyncSimpleModal, getRandomNumber, setTrim } from '../../../../../lib/common';
import { compareProductVersion } from '../../../../../lib/utils';
import { PRODUCT_OPTION_ITEM_MODEL } from '../../../../../store/modules/product/models';
import { productActions } from '../../../../../store/modules/product';
import {
  FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST,
  MAXIMUM_NUMBER_OF_PRODUCT_OPTION_NAME_TEXT,
  MAXIMUM_NUMBER_OF_PRODUCT_OPTION_VALUE_CSV_TEXT,
  MAXIMUM_NUMBER_OF_PRODUCT_OPTION_VALUE_TEXT,
  PRODUCT_OPTION_LIST_TABLE_EMPTY_TEXT,
  PRODUCT_OPTION_NAME_SELECT_LIST,
} from '../../../../../constants/service/app/productConstants';
import './ProductOptionInfo.scss';

const ProductOptionInfo = (props) => {
  const {
    form,
    isEditable,
    isCompare,
    comparisonData,
    choiceOption,
    setChoiceOption,
    optionList,
    setOptionList,
    optionKeyName,
    setOptionKeyName,
    minimumPrice,
    brandFeeInfoList,
    resetOptionAvailable,
    setIsGift,
  } = props;
  const [isShowFeeInfo, setIsShowFeeInfo] = useState(false);
  const dispatch = useDispatch();
  const { isOpen } = useSelector((state) => state.product.editProductOptionOrderModal);
  const { salesPrice } = form.getFieldsValue();

  useEffect(() => {
    form.setFieldsValue({ choiceOption });
  }, [choiceOption]);

  useEffect(() => {
    form.setFieldsValue({ optionList });
  }, [optionList]);

  useEffect(() => {
    form.setFieldsValue({ optionKeyName });
  }, [optionKeyName]);

  const applyOptions = async () => {
    let { id, name, value } = choiceOption;

    /**
     *  상품 편집 페이지에서 풀필먼트 상품 타입 옵션이 1개 이상 있는 경우
     *  옵션적용 기능 막기
     */
    if (!resetOptionAvailable) {
      await asyncSimpleModal({
        type: 'error',
        title: '옵션 적용 불가',
        message: '풀필먼트 옵션이 있는 경우 옵션 적용이 불가능합니다.',
      });
      return;
    }

    /**
     *  선택된 옵션 키와 옵션명(CSV)의 앞뒤 공백 제거하기
     */
    if (!id) name = setTrim(name);
    value = setTrim(value);

    /**
     *  옵션명 체크
     */
    if (!id && (!name || name === '')) {
      message.error('옵션명을 입력하시기 바랍니다.');
      return;
    }

    /**
     *  옵션값 체크
     */
    if (!value || value === '') {
      message.error('옵션값을 입력하시기 바랍니다.');
      return;
    }

    /**
     *  옵션 리스트
     */
    let optionValueList = value.split(',');
    /**
     *  옵션 값 공백 제거
     */
    optionValueList = optionValueList.map((item) => setTrim(item));

    /**
     *  옵션 값 중복 제거
     */
    optionValueList = uniq(optionValueList);

    /**
     *  옵션 값 길이 제한 체크
     */
    optionValueList = optionValueList.filter((part) => {
      if (part.length > MAXIMUM_NUMBER_OF_PRODUCT_OPTION_VALUE_TEXT) {
        message.error('옵션 값은 50자 이내로 입력하시기 바랍니다.');
        return false;
      } else if (part === '') return false;
      else return true;
    });

    if (!optionValueList) return;

    const isFulfillmentProduct = brandFeeInfoList.find((item) =>
      FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST.includes(item.productManagementType),
    );

    const optionList = optionValueList.map((name, index) => {
      const productOptionItemModel = { ...PRODUCT_OPTION_ITEM_MODEL };
      const targetFee = brandFeeInfoList.find(
        (item) => item.productManagementType === productOptionItemModel.productManagementType,
      );

      productOptionItemModel.id = getRandomNumber();
      productOptionItemModel.name = setTrim(name);
      productOptionItemModel.no = index + 1;
      productOptionItemModel.feeInfo = {
        ...productOptionItemModel.feeInfo,
        ...omit(targetFee, ['productManagementType']),
      };
      productOptionItemModel.productManagementTypeSelectList = isFulfillmentProduct
        ? productOptionItemModel.productManagementTypeSelectList.map((item) => ({ ...item, disabled: false }))
        : productOptionItemModel.productManagementTypeSelectList;
      productOptionItemModel.isNew = true;

      return productOptionItemModel;
    });

    setOptionList(optionList);

    /**
     *  옵션명
     */
    let optionKeyName = null;
    /**
     *  SELECT 리스트에서 선택한 경우
     */
    if (id) optionKeyName = PRODUCT_OPTION_NAME_SELECT_LIST[id].name;
    /**
     *  직접입력
     */ else optionKeyName = name;
    setOptionKeyName(optionKeyName);
  };

  const addOption = () => {
    /**
     *  옵션명이 있어야만 옵션 목록을 추가 할 수 있다.
     */
    if (!optionKeyName) {
      message.error('옵션명을 먼저 입력하시기 바랍니다.');
      return;
    }

    const defaultFeeInfo = brandFeeInfoList.find(
      (item) => item.productManagementType === PRODUCT_OPTION_ITEM_MODEL.productManagementType,
    );
    const isFulfillmentProduct = brandFeeInfoList.find((item) =>
      FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST.includes(item.productManagementType),
    );

    const productOptionItemModel = {
      ...PRODUCT_OPTION_ITEM_MODEL,
      productManagementTypeSelectList: isFulfillmentProduct
        ? PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList.map((item) => ({ ...item, disabled: false }))
        : PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList,
      feeInfo: {
        ...PRODUCT_OPTION_ITEM_MODEL.feeInfo,
        ...omit(defaultFeeInfo, ['productManagementType']),
      },
    };
    productOptionItemModel.id = getRandomNumber();
    productOptionItemModel.isNew = true;
    productOptionItemModel.no = optionList.length + 1;

    setOptionList([...optionList, productOptionItemModel]);
  };

  const deleteOption = async (targetId) => {
    let targetOptionList = optionList.filter((item) => item.id !== targetId);

    const confirmResult = await asyncSimpleModal({
      type: 'confirm',
      title: '옵션 삭제',
      message: '정말로 옵션을 삭제하시겠습니까?',
    });

    /**
     *  옵션 리스트에서 해당 옵션 삭제하기
     */
    if (confirmResult) {
      setOptionList(targetOptionList);
    }
  };

  const editProductOptionOrder = () => {
    dispatch(
      productActions.toggleModal({
        name: 'editProductOptionOrderModal',
        value: true,
        data: {
          form: form,
          list: [],
        },
      }),
    );
  };

  const setProductOptionProductManagementType = (data) => {
    const { index, value } = data;

    const defaultFeeInfo = brandFeeInfoList.find((item) => item.productManagementType === value);

    setOptionList((prev) => {
      let next = [...prev];
      next[index] = {
        ...prev[index],
        productManagementType: value,
        feeInfo: {
          ...prev[index].feeInfo,
          ...omit(defaultFeeInfo, ['productManagementType']),
        },
      };
      return [...next];
    });
  };

  return (
    <>
      <BaseForm
        label="옵션 입력"
        content={
          <div className="product-option-manager">
            <div className="option-name">
              <div className="text">옵션명</div>
              <div className="option-name-input-wrap">
                <Select
                  isEditable={isEditable}
                  list={PRODUCT_OPTION_NAME_SELECT_LIST}
                  onChange={(id) => setChoiceOption((prev) => ({ ...prev, id }))}
                  defaultValue={choiceOption.id}
                  value={choiceOption.id}
                  isTable
                  size="large"
                />
                {!choiceOption.id && (
                  <Input
                    className="choice-option-key-name-input"
                    isEditable={isEditable}
                    value={choiceOption.name}
                    onChange={(name) => setChoiceOption((prev) => ({ ...prev, name }))}
                    maxLength={MAXIMUM_NUMBER_OF_PRODUCT_OPTION_NAME_TEXT}
                    size="large"
                  />
                )}
              </div>
              {isEditable ? (
                <Button
                  type="button"
                  className="submit-to-list-btn"
                  isRadius={true}
                  color="primary"
                  disabled={!isEditable}
                  onClick={applyOptions}
                  text="옵션목록으로 적용"
                />
              ) : null}
            </div>
            <div className="option-value">
              <div className="text">옵션값</div>
              <div className="option-value-input-wrap">
                <Input
                  isEditable={isEditable}
                  value={choiceOption.value}
                  onChange={(value) => setChoiceOption((prev) => ({ ...prev, value }))}
                  maxLength={MAXIMUM_NUMBER_OF_PRODUCT_OPTION_VALUE_CSV_TEXT}
                  size="large"
                />
              </div>
            </div>
          </div>
        }
      />
      <div className="option-list-table-wrap">
        {isEditable ? (
          <Fragment>
            <Button
              type="button"
              className="add-option-item"
              isRadius={true}
              color="primary"
              disabled={!isEditable}
              onClick={addOption}
              text="+목록 추가"
            />

            {optionList && optionList.length ? (
              <Button
                type="button"
                className="edit-option-item-order"
                isRadius={true}
                color="light"
                disabled={!isEditable}
                onClick={editProductOptionOrder}
                text="옵션 순서 변경"
              />
            ) : null}
          </Fragment>
        ) : null}

        <Table
          className={cx(
            'option-list-table',
            isCompare && compareProductVersion(optionList, comparisonData),
            isCompare ? 'version-compare' : '',
          )}
          emptyText={PRODUCT_OPTION_LIST_TABLE_EMPTY_TEXT}
          data={optionList}
          isEditable={isEditable}
          columns={(utils) =>
            columns({
              form,
              deleteOption,
              salesPrice,
              optionKeyName,
              minimumPrice,
              isEditable,
              toggleFeeInfo: () => setIsShowFeeInfo((prev) => !prev),
              isHideFeeInfo: optionList.length ? isShowFeeInfo : undefined,
              setProductOptionProductManagementType,
              setOptionList,
              setIsGift,
              ...utils,
            })
          }
        />
      </div>
      <div className="hidden-form">
        <InputForm form={form} type="hidden" name="choiceOption" initialValue={choiceOption} />
        <InputForm form={form} type="hidden" name="optionKeyName" initialValue={optionKeyName} />
        <InputForm form={form} type="hidden" name="optionList" initialValue={optionList} />
      </div>
      {isOpen && (
        <EditProductOptionOrderModal
          isOpen={isOpen}
          form={form}
          optionKeyName={optionKeyName}
          optionList={optionList}
          setOptionList={setOptionList}
        />
      )}
    </>
  );
};

export default ProductOptionInfo;
